import {
	useState,
	useRef
} from 'react'
import {
	useDispatch,
	useSelector
} from 'react-redux'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Overlay from 'react-bootstrap/Overlay'
import Popover from 'react-bootstrap/Popover'
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import moment from 'moment'

import {
	toggleWeekdayAvailabilities,
	getAllEventsInRange
} from '../../state/schedulerSlice'

import {dateToYMD,addZero} from '../../util/datetime'

import {
	useWeekdayAvailability,
	useWeekdayAvailabilities,
	useWeekdayAvailabilityRange,
	useWeekdayAvailabilityMutations,
	useWeekdayAvailabilityRangeMutations
} from './hooks'

import TimePicker from '../TimePicker/TimePicker'

const WeekdayAvailabilityRange = ({id})=>{
	const isTime24Hour = Number(useSelector(state => state.users.current_user.is_24_hour_time))
	const availabilityRange = useWeekdayAvailabilityRange(id)

	const {
		edit:editMutation,
		remove:removeMutation
	} = useWeekdayAvailabilityRangeMutations()

	const editStartTime = start_time => {
		editMutation({
			update:{start_time},
			id
		})
	}

	const editEndTime = end_time => {
		editMutation({
			update:{end_time},
			id
		})
	}

	const handleRemove = ()=>{
		removeMutation(id)
	}

	const [startHour,startMinute,startSeconds] = availabilityRange.start_time.split(':')
	const startPeriod = startHour < 12 ? 'a' : 'p'
	const [endHour,endMinute,endSeconds] = availabilityRange.end_time.split(':')
	const endPeriod = endHour < 12 ? 'a' : 'p'

	return (
		<div className="d-flex mb-2 align-items-center flex-wrap">
			<TimePicker
				onChange={newTimeObj=>{
					const newStartTime = `${addZero(newTimeObj.hour)}:${addZero(newTimeObj.min)}:00`
					editStartTime(newStartTime)
				}}
				minuteInterval={15}
				hour={startHour}
				minute={startMinute}
				period={startPeriod}
				isTime24Hour={isTime24Hour}
			/>
			
			<small className="mx-2">-</small>
			<TimePicker
				onChange={newTimeObj=>{
					const newEndTime = `${addZero(newTimeObj.hour)}:${addZero(newTimeObj.min)}:00`
					editEndTime(newEndTime)
				}}
				minuteInterval={15}
				hour={endHour}
				minute={endMinute}
				period={endPeriod}
				isTime24Hour={isTime24Hour}
			/>
			<Button 
				onClick={handleRemove}
				variant="outline-danger"
				className="ml-1"
				size="sm"
			>
				<i className="fa fa-trash"/>
			</Button>
		</div>
	)
}

const CopyAvailabilityRangesButton = ({weekdayAvailabilityId})=>{
	const weekdays = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']
	const [selectedDays,setSelectedDays] = useState([])

	const buttonRef = useRef(null)
	const [showPopover,setShowPopover] = useState(false)

	const {
		copy:copyRangesMutation
	} = useWeekdayAvailabilityRangeMutations()

	const toggleSelectedDay = day => {
		setSelectedDays(prev=>(
			prev.includes(day)
			? prev.filter( d => d !== day )
			: [...prev,day]
		))
	}

	const togglePopover = ()=>{
		setShowPopover(p=>!p)
	}

	const [copyStatus,setCopyStatus] = useState('idle')
	const handleCopy = ()=>{
		setCopyStatus('pending')
		copyRangesMutation({
			weekday_availability_id:weekdayAvailabilityId,
			tgt_weekdays:selectedDays
		}).then(()=>{
			setSelectedDays([])
			setShowPopover(false)
			setCopyStatus('idle')
		})
	}

	return (
		<>
			<Button 
				onClick={togglePopover}
				ref={buttonRef}
				variant="outline-primary"
				size="sm"
			>
				<i className="fa fa-copy"/>
			</Button>
			<Overlay 
				onHide={togglePopover}
				rootClose 
				target={buttonRef.current} 
				show={showPopover} 
				placement="left"
			>
				{(props) => (
					<Popover {...props}>
						<Popover.Content className="px-4">
							<small className="text-muted">
								Copy Times To:
							</small>
							{weekdays.map((weekday,i)=>(
								<Form.Check 
									onChange={()=>{
										toggleSelectedDay(i)
									}}
									checked={selectedDays.includes(i)}
									className="mb-2 block w-100"
									key={weekday} 
									label={weekday} 
									id={`weekday-copy-check-${weekday}`}
								/>
							))}
							<Button 
								disabled={copyStatus==='pending'}
								onClick={handleCopy}
								className="px-4" 
								block 
								variant="outline-primary" 
								size="sm"
							>
								Copy Times {copyStatus==='pending'&&<Spinner size="sm" animation="border"/>}
							</Button>
						</Popover.Content>
					</Popover>
				)}
			</Overlay>
		</>
	)
}

const WeekdayAvailability = ({id})=>{
	const availability = useWeekdayAvailability(id)
	const {edit:editMutation} = useWeekdayAvailabilityMutations()
	const {
		create:createRangeMutation
	} = useWeekdayAvailabilityRangeMutations()
	const handleToggleAvailability = e => {
		editMutation({
			update:{
				is_available:Number(e.target.checked)
			},
			id
		})
	}
	const [addRangeStatus,setAddRangeStatus] = useState('idle')
	const handleAddRange = ()=>{
		setAddRangeStatus('pending')
		createRangeMutation({
			weekday_availability_id:id
		}).then(()=>{
			setAddRangeStatus('idle')
		})
	}

	

	return (
		<div 
			style={{borderBottom:'1px solid #ccc'}} 
			className="d-flex justify-content-between mb-2 pb-2"
		>
			<div className="d-flex">
				<Form.Check 
					onChange={handleToggleAvailability}
					checked={Boolean(availability.is_available)}
					id={`weekday-check-${availability.id}`}
					label={availability.weekday}
					style={{width:'80px'}}
				/>
				<div>
					{Boolean(availability.is_available)&&
						availability.ranges.map( id => (
							<WeekdayAvailabilityRange key={id} id={id}/>
						))
					}
					{!Boolean(availability.is_available)&&
						<small className="text-muted">Unavailable</small>
					}
				</div>
			</div>
			{Boolean(availability.is_available)&&
				<div>
					<Button 
						disabled={addRangeStatus==='pending'}
						onClick={handleAddRange}
						variant="outline-primary"
						className="mr-1"
						size="sm"
					>
						{
							addRangeStatus==='pending'
							? <Spinner animation="border" size="sm"/>
							: <i className="fa fa-plus"/>
						}
					</Button>
					<CopyAvailabilityRangesButton weekdayAvailabilityId={id}/>
				</div>
			}
		</div>
	)
}

const AvailabilityModal = ()=>{
	const dispatch = useDispatch()

	const refreshCalendar = ()=>{
		//start of last month
		const date = new Date()
		const startLastMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1)
		dispatch(getAllEventsInRange({
			action: "getAllEventsInRange",
			start_date: dateToYMD(startLastMonth),
			offset: 0,
			//get at least 7 days into next month
			//7 to account for -1
			//31 to get all events in current month
			//7 to get 7 days into next month
			//7 + 31 + 7 = 45
			limit: 90,
		}))
	}

	const handleClose = ()=>{
		dispatch(toggleWeekdayAvailabilities(false))
		refreshCalendar()
	}

	const {show,status,ids} = useWeekdayAvailabilities()

	return (
		<Modal show={show} onHide={handleClose} size="lg">
			<Modal.Header closeButton>
				<strong>Set your weekly hours</strong>
			</Modal.Header>
			<Modal.Body>
				{
					status === 'pending'
					? <Spinner size="sm" animation="border"/>
					: ids.map( id => <WeekdayAvailability key={id} id={id} /> )
				}
			</Modal.Body>
		</Modal>
	)
}

export default AvailabilityModal